import { createContext, useContext, useState, useMemo, useCallback, useEffect } from 'react';
import { useGalleryConfig } from './GalleryConfigContext.jsx';
import { useAuth } from './AuthContext.jsx';
import { TESTIMONIALS } from '../data/testimonials.js';
import { MODELS as STATIC_MODELS } from '../data/galeria.js';

const STORAGE_KEY = 'tmstudio_home_config';

const defaultConfig = {
  galeria: [],
  estudio: [0, 1, 2, 3, 4, 5, 6],
  beneficios: [],
  testimonials: TESTIMONIALS.map((t) => t.id),
  customTestimonials: [],
  testimonialEdits: {},
};

function loadFromStorage() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      return {
        galeria: Array.isArray(parsed.galeria) ? parsed.galeria : defaultConfig.galeria,
        estudio: Array.isArray(parsed.estudio) ? parsed.estudio : defaultConfig.estudio,
        beneficios: Array.isArray(parsed.beneficios) ? parsed.beneficios : defaultConfig.beneficios,
        testimonials: Array.isArray(parsed.testimonials) ? parsed.testimonials : defaultConfig.testimonials,
        customTestimonials: Array.isArray(parsed.customTestimonials) ? parsed.customTestimonials : [],
        testimonialEdits: parsed.testimonialEdits && typeof parsed.testimonialEdits === 'object' ? parsed.testimonialEdits : {},
      };
    }
  } catch (_) {}
  return { ...defaultConfig };
}

function saveToStorage(config) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  } catch (_) {}
}

const HomeConfigContext = createContext(null);

export function HomeConfigProvider({ children }) {
  const [config, setConfigState] = useState(loadFromStorage);
  const [loading, setLoading] = useState(true);
  const { models: galleryModels } = useGalleryConfig();
  const { getAuthHeader } = useAuth();

  useEffect(() => {
    let cancelled = false;
    fetch('/api/homeConfig')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (cancelled) return;
        if (data && typeof data === 'object') {
          const merged = {
            galeria: Array.isArray(data.galeria) ? data.galeria : defaultConfig.galeria,
            estudio: Array.isArray(data.estudio) ? data.estudio : defaultConfig.estudio,
            beneficios: Array.isArray(data.beneficios) ? data.beneficios : defaultConfig.beneficios,
            testimonials: Array.isArray(data.testimonials) ? data.testimonials : defaultConfig.testimonials,
            customTestimonials: Array.isArray(data.customTestimonials) ? data.customTestimonials : [],
            testimonialEdits: data.testimonialEdits && typeof data.testimonialEdits === 'object' ? data.testimonialEdits : {},
          };
          setConfigState(merged);
          saveToStorage(merged);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  const setConfig = useCallback((updater) => {
    setConfigState((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      saveToStorage(next);
      return next;
    });
  }, []);

  const saveConfigToApi = useCallback(async (cfg) => {
    const payload = cfg ?? loadFromStorage();
    const res = await fetch('/api/homeConfig', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
      },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.error || 'Error al guardar');
    }
    const data = await res.json();
    if (data && typeof data === 'object') {
      setConfigState(data);
      saveToStorage(data);
    }
  }, [getAuthHeader]);

  return (
    <HomeConfigContext.Provider value={{ config, setConfig, galleryModels, saveConfigToApi, loading }}>
      {children}
    </HomeConfigContext.Provider>
  );
}

export function useHomeConfig() {
  const ctx = useContext(HomeConfigContext);
  if (!ctx) return { config: defaultConfig, setConfig: () => {}, galleryModels: [], saveConfigToApi: async () => {} };
  return ctx;
}

export function useModelsForHome() {
  const { config, galleryModels } = useHomeConfig();
  const ids = config.galeria;
  if (!ids || ids.length === 0) return galleryModels;
  return galleryModels.filter((m) => ids.includes(m.id));
}

export function useEstudioIndicesForHome() {
  const { config } = useHomeConfig();
  return config.estudio;
}

export function useBenefitsImagesForHome() {
  const { config, galleryModels } = useHomeConfig();
  const ids = config.beneficios;
  if (ids && ids.length >= 4) {
    const arr = ids.map((id) => galleryModels.find((m) => m.id === id)?.src).filter(Boolean);
    if (arr.length >= 4) return arr;
  }
  return galleryModels.slice(0, 4).map((m) => m.src);
}

function resolveTestimonial(t, galleryModels, edits) {
  const override = edits?.[t.id];
  const mid = override?.modelId ?? t.modelId;
  const model = galleryModels.find((m) => m.id === mid);
  const staticModel = typeof mid === 'number' && mid >= 1 && mid <= 27 ? STATIC_MODELS.find((m) => m.id === mid) : null;
  const src = model?.src || staticModel?.src || '';
  const name = override?.name || model?.name || staticModel?.name || 'Modelo';
  return {
    ...t,
    name,
    image: src,
    tag: override?.tag || t.tag,
    preview: override?.preview || t.preview,
    experience: override?.experience || t.experience,
  };
}

function resolveCustomTestimonial(ct, galleryModels) {
  const model = ct.modelId ? galleryModels.find((m) => m.id === ct.modelId) : null;
  const staticModel = typeof ct.modelId === 'number' && ct.modelId >= 1 && ct.modelId <= 27
    ? STATIC_MODELS.find((m) => m.id === ct.modelId) : null;
  const src = model?.src || staticModel?.src || ct.image || '';
  const name = ct.name || model?.name || staticModel?.name || 'Modelo';
  return { ...ct, image: src, name };
}

export function useAllTestimonials() {
  const { config, galleryModels } = useHomeConfig();

  return useMemo(() => {
    const base = TESTIMONIALS.map((t) => resolveTestimonial(t, galleryModels, config.testimonialEdits));
    const custom = (config.customTestimonials || []).map((ct) => resolveCustomTestimonial(ct, galleryModels));
    return [...base, ...custom];
  }, [config.testimonialEdits, config.customTestimonials, galleryModels]);
}

export function useTestimonialsForHome() {
  const { config, galleryModels } = useHomeConfig();
  const ids = config.testimonials;

  return useMemo(() => {
    const base = TESTIMONIALS.map((t) => resolveTestimonial(t, galleryModels, config.testimonialEdits));
    const custom = (config.customTestimonials || []).map((ct) => resolveCustomTestimonial(ct, galleryModels));
    const all = [...base, ...custom];
    if (!ids?.length) return all;
    return all.filter((t) => ids.includes(t.id));
  }, [ids, config.testimonialEdits, config.customTestimonials, galleryModels]);
}
